import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable } from 'rxjs';

import { HttpParams } from '@angular/common/http';
import { EmployeeConsultantsService } from '../../../../../services/api/hr/employee-consultants.service';
import { CompaniesService } from '../../../../../services/api/general-settings/companies.service';
import { AlertInfo } from '../../../../../models/alert-info';
import { SavedDataInfo } from '../../../../../models/form/saved-data-info';
import { InputFieldComponent } from '../../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../../components/form/select-field/select-field.component';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { OptionService } from '../../../../../services/api/option.service';

const params = new HttpParams().set('size', '9999');

@Component({
  selector: 'employee-consultants-general-info-form',
  imports: [ReactiveFormsModule, NgbAlert, InputFieldComponent, SelectFieldComponent],
  templateUrl: './employee-consultants-general-info-form.component.html',
  styleUrl: './employee-consultants-general-info-form.component.scss'
})
export class EmployeeConsultantsGeneralInfoFormComponent implements OnInit, OnChanges {
  @Input() employee: any;
  @Input() readonly: boolean = false;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;
  alertInfo: AlertInfo = { show: false, type: 'success', message: '' };
  locations: any[] = [];
  contractTypes: any[] = [];
  statusOptions: any[] = [];
  specializations: any[] = [];
  selectedSpecializationsCount: number = 0;
  loading: boolean = false;
  validationErrors: any[] = []; 

  constructor(
    private fb: FormBuilder,
    private employeeConsultantsService: EmployeeConsultantsService,
    private optionService: OptionService,
    private companiesService: CompaniesService
  ) {
    this.initForm();
  }



  ngOnInit(): void {
    
    const params = new HttpParams().set('size', '9999');
    this.companiesService.list({ params }).subscribe({
      next: (response: any) => {
        if (response?.data?.companies) {
          this.locations = response.data.companies.map((company: any) => ({
            key: company.companyId,
            value: company.name
          }));
        }
      },
      error: (error: any) => {
        console.error('Error loading companies locations:', error);
      }
    });

    this.optionService.employeeContractType().subscribe({
      next: (response: any) => {
        if (response?.data?.options) {          
          this.contractTypes = response.data.options.map((item: any) => ({
            key: item.key,
            value: item.value
          }));
        }
      },
      error: (error: any) => {
        console.error('Error loading contract types:', error);
      }
    });

    this.optionService.employeeStatus().subscribe({
      next: (response: any) => {
        if (response?.data?.options) {
          this.statusOptions = response.data.options.map((item: any) => ({
            key: item.key,
            value: item.value
          }));
        }
      },
      error: (error: any) => {
        console.error('Error loading employee statuses:', error);
      }
    });

    this.employeeConsultantsService.listSpecialisations({params}).subscribe({
      next: (response: any) => {
        if (response?.data?.employeeSpecialisations) {
          this.specializations = response.data.employeeSpecialisations.map((specialty: any) => ({
            employeeSpecialisationId: specialty.employeeSpecialisationId,
            code: specialty.code,
            name: specialty.name
          }));
          
          if (this.employee?.employeeId) {
            this.loadEmployeeSpecializations(this.employee.employeeId);
          }
        }
      },
      error: (error: any) => {
        console.error('Error loading specializations:', error);
      }
    });

    if (this.employee) {
      this.updateFormWithEmployeeData();
    }
  }

    ngOnChanges(changes: SimpleChanges): void {
    if (changes['employee'] && changes['employee'].currentValue) {
      console.log('Employee input changed:', changes['employee'].currentValue);
      
      if (this.specializations.length <= 0) {
        this.initializeSelectedSpecializationsCount();
      }
      
      this.updateFormWithEmployeeData();
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      code: ['', Validators.required],
      workingCompanyId: [null, Validators.required],
      engageTime: ['', Validators.required],
      endTime: [''],
      role: ['', Validators.required],
      actitude: [''],
      contractType: ['', Validators.required],
      status: ['', Validators.required],
      employeeSpecializations: this.fb.array([])
    });

    if (this.readonly) {
      this.form.disable();
    }
  }

  private updateFormWithEmployeeData(): void {
    if (this.employee) {
      this.form.patchValue({
        code: this.employee.code || '',
        workingCompanyId: this.employee.workingCompany.companyId || null,
        engageTime: this.employee.engageTime || '',
        endTime: this.employee.endTime || '',
        role: this.employee.role || '',
        actitude: this.employee.actitude || '',
        contractType: this.employee.contractType || 'consultant',
        status: this.employee.status || 'active'
      });

      if (this.employee.employeeId) {
        this.loadEmployeeSpecializations(this.employee.employeeId);
      }
    }
  }

  loadEmployeeSpecializations(employeeId: number): void {
    this.employeeConsultantsService.listEmployeeSpecialisations(employeeId).subscribe({
      next: (response: any) => {
        if (response?.data?.employeeSpecialisations) {
          if (!this.employee.employeeJoinEmployeeSpecialisationList) {
            this.employee.employeeJoinEmployeeSpecialisationList = [];
          }          
          this.employee.employeeJoinEmployeeSpecialisationList = response.data.employeeSpecialisations.map((specialty: any) => ({
            employeeSpecialisationId: specialty.employeeSpecialisationId
          }));
          
          this.initializeSelectedSpecializationsCount();
        }
      },
      error: (error: any) => {
        console.error('Error loading employee specializations:', error);
      }
    });
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }

  updateValidationErrors(errors: any[]): void {
    this.validationErrors = errors;
    errors.forEach(error => {
      const control = this.form.get(error.field);
      if (control) {
        control.setErrors({ serverError: error.message });
      }
    });
  }

  getSelectedSpecializations(): number[] {
    if (!this.employee?.employeeJoinEmployeeSpecialisationList) return [];
    return this.employee.employeeJoinEmployeeSpecialisationList.map((item: any) => item.employeeSpecialisationId);
  }

  isSpecializationSelected(specializationId: number): boolean {
    return this.getSelectedSpecializations().includes(specializationId);
  }

  initializeSelectedSpecializationsCount(): void {
    if (this.employee?.employeeJoinEmployeeSpecialisationList?.length > 0) {
      this.selectedSpecializationsCount = this.employee.employeeJoinEmployeeSpecialisationList.length;
      console.log('Specializations count initialized:', this.selectedSpecializationsCount);
    } else {
      this.selectedSpecializationsCount = 0;
      console.log('No specializations found, count set to 0');
    }
  }

  getSelectedSpecializationsCount(): number {
    return this.selectedSpecializationsCount;
  }

  toggleSpecialization(specializationId: number): void {
    if (!this.employee) {
      this.employee = {
        employeeJoinEmployeeSpecialisationList: []
      };
    }
    
    if (!this.employee.employeeJoinEmployeeSpecialisationList) {
      this.employee.employeeJoinEmployeeSpecialisationList = [];
    }
    
    const isSelected = this.isSpecializationSelected(specializationId);
    
    if (isSelected) {
      this.employee.employeeJoinEmployeeSpecialisationList = this.employee.employeeJoinEmployeeSpecialisationList
        .filter((item: any) => item.employeeSpecialisationId !== specializationId);
      this.selectedSpecializationsCount = Math.max(0, this.selectedSpecializationsCount - 1);
    } else {
      this.employee.employeeJoinEmployeeSpecialisationList.push({
        employeeSpecialisationId: specializationId
      });
      this.selectedSpecializationsCount++;
    }
    
    setTimeout(() => {
      this.selectedSpecializationsCount = this.getSelectedSpecializations().length;
    }, 0);
  }

  validateForm(): boolean {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        control?.markAsTouched();
      });
      this.showAlertInfo('danger', 'Please fill in all required fields correctly.');
      return false;
    }
    return true;
  }

  saveEmployeeSpecializations(employeeId: number): Observable<any> {
    const specializationData = {
      tenantId: 1,
      employeeSpecialisationIds: this.getSelectedSpecializations()
    };
    
    return this.employeeConsultantsService.updateEmployeeSpecialisation(employeeId, specializationData);
  }

  saveForm() {
    this.loading = true;
    const employeeData = { ...this.form.value };
    
    if (employeeData.workingCompanyId) {
      employeeData.workingCompany = {
        workingCompanyId: employeeData.workingCompanyId
      };
    }

    if (this.employee?.employeeId) {
      return this.employeeConsultantsService.update(this.employee.employeeId, employeeData);
    } else {
      return this.employeeConsultantsService.create(employeeData);
    }
  }

  onSubmit(): void {
    if (this.loading) return;

    if (!this.validateForm()) return;

    this.loading = true;
    this.saveForm().subscribe({
      next: (response: any) => {
        const employeeId = response?.data?.employee?.employeeId || this.employee?.employeeId;
        
        if (employeeId) {
          this.saveEmployeeSpecializations(employeeId).subscribe({
            next: (specResponse: any) => {
              this.loading = false;
              this.showAlertInfo('success', 'Employee general information and specializations updated successfully');
              this.onSavedData.emit({ success: true, response });
            },
            error: (specError: any) => {
              this.loading = false;
              this.showAlertInfo('danger', specError.error?.message || 'Error updating employee specializations');
              this.onSavedData.emit({ success: false, error: specError });
            }
          });
        } else {
          this.loading = false;
          this.showAlertInfo('success', 'Employee general information updated successfully');
          this.onSavedData.emit({ success: true, response });
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.showAlertInfo('danger', error.error?.message || 'An error occurred while updating employee information');
        this.onSavedData.emit({ success: false, error });
      }
    });
  }

  getEmployeeCode(): FormControl { return this.getFormControl('code'); }
  getEmployeeCompanyLocationSelect(): FormControl { return this.getFormControl('workingCompanyId'); }
  getEmployeeEngageTimeInput(): FormControl { return this.getFormControl('engageTime'); }
  getEmployeeEndTimeInput(): FormControl { return this.getFormControl('endTime'); }
  getEmployeeRoleInput(): FormControl { return this.getFormControl('role'); }
  getEmployeeActitudeInput(): FormControl { return this.getFormControl('actitude'); }
  getEmployeeContractTypeSelect(): FormControl { return this.getFormControl('contractType'); }
  getEmployeeStatusSelect(): FormControl { return this.getFormControl('status'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }

}
